/**
 * DEV-2018 (эпик DEV-2005). Видеовстречи с клиентом: назначение, доставка
 * приглашения, записи с расшифровками и черновик квалификации.
 *
 * Слой поверх сгенерированного клиента: карточкам нужны плоские структуры, а не
 * `Meeting | undefined` с полями `string | undefined` в каждом поле. Заодно
 * здесь остаются доменные значения перечислений — компоненты сравнивают статусы,
 * а не строки протокола.
 */
import { meetingsApi } from '@/api'
import type {
  MeetingsMeeting,
  MeetingsMeetingInsights,
  MeetingsMeetingInvite,
  MeetingsMeetingRecording,
  MeetingsMeetingTranscript,
  MeetingsQualificationDraft,
  MeetingsQualificationOverride,
  MeetingsQualificationSuggestion,
  MeetingsScheduleMeetingRequest,
  MeetingsServiceListMeetingsStatusesEnum,
} from '@/api/clients/meetings'
import {
  MeetingsInviteChannel,
  MeetingsInviteStatus,
  MeetingsLeadTemperature,
  MeetingsMeetingProvider,
  MeetingsMeetingSortBy,
  MeetingsMeetingStatus,
  MeetingsQualificationStatus,
  MeetingsRecordingProcessingStatus,
  MeetingsSentiment,
} from '@/api/clients/meetings'

export {
  MeetingsInviteChannel as InviteChannel,
  MeetingsInviteStatus as InviteStatus,
  MeetingsLeadTemperature as LeadTemperature,
  MeetingsMeetingProvider as MeetingProvider,
  MeetingsMeetingSortBy as MeetingSortBy,
  MeetingsMeetingStatus as MeetingStatus,
  MeetingsQualificationStatus as QualificationStatus,
  MeetingsRecordingProcessingStatus as RecordingStatus,
  MeetingsSentiment as Sentiment,
}

export type Meeting = MeetingsMeeting
export type MeetingInsights = MeetingsMeetingInsights
export type MeetingInvite = MeetingsMeetingInvite
export type MeetingRecording = MeetingsMeetingRecording
export type MeetingTranscript = MeetingsMeetingTranscript
export type QualificationDraft = MeetingsQualificationDraft
export type QualificationSuggestion = MeetingsQualificationSuggestion
export type QualificationOverride = MeetingsQualificationOverride

/**
 * Фильтр раздела «Видеовстречи» (DEV-2086) в терминах API. Собирается из
 * фильтров экрана утилитой meetingsList — компоненты значений протокола не
 * знают.
 */
export interface MeetingsListQuery {
  clientId?: string
  dealId?: string
  managerId?: string
  providers?: MeetingsMeetingProvider[]
  statuses?: MeetingsMeetingStatus[]
  startFrom?: string
  startTo?: string
  leadTemperatures?: MeetingsLeadTemperature[]
  minUsefulness?: number
  clientQuery?: string
  sortBy?: MeetingsMeetingSortBy
  sortDesc?: boolean
  page?: number
  perPage?: number
}

export interface MeetingsPage {
  meetings: Meeting[]
  total: number
  page: number
  perPage: number
}

/** Сводка над списком: числа, а не строки протокола (uint64 приходит строкой). */
export interface MeetingsStats {
  total: number
  hotLeads: number
  awaitingQualification: number
  avgUsefulness: number
  ratedCount: number
  /** Встречи с непринятыми предложениями ИИ в карточку — бейдж меню (DEV-2826). */
  pendingSuggestions: number
}

/** Страница списка раздела: фильтры, сортировка и пагинация — всё на сервере. */
export async function listMeetingsPage(query: MeetingsListQuery): Promise<MeetingsPage> {
  const { data } = await meetingsApi.meetingsServiceListMeetings(
    query.clientId,
    query.dealId,
    query.managerId,
    query.providers,
    query.statuses,
    query.startFrom,
    query.startTo,
    query.sortDesc,
    query.page ? String(query.page) : undefined,
    query.perPage ? String(query.perPage) : undefined,
    query.leadTemperatures,
    query.minUsefulness,
    query.clientQuery,
    query.sortBy,
  )

  return {
    meetings: data.meetings ?? [],
    total: Number(data.pagination?.total ?? 0),
    page: Number(data.pagination?.page ?? query.page ?? 1),
    perPage: Number(data.pagination?.perPage ?? query.perPage ?? 20),
  }
}

/** Сводка по тому же фильтру, что и список: считает сервер тем же скоупом. */
export async function getMeetingsStats(query: MeetingsListQuery): Promise<MeetingsStats> {
  const { data } = await meetingsApi.meetingsServiceGetMeetingsStats(
    query.clientId,
    query.dealId,
    query.managerId,
    query.providers,
    query.statuses,
    query.startFrom,
    query.startTo,
    query.leadTemperatures,
    query.minUsefulness,
    query.clientQuery,
  )

  return {
    total: Number(data.total ?? 0),
    hotLeads: Number(data.hotLeads ?? 0),
    awaitingQualification: Number(data.awaitingQualification ?? 0),
    avgUsefulness: Number(data.avgUsefulness ?? 0),
    ratedCount: Number(data.ratedCount ?? 0),
    pendingSuggestions: Number(data.pendingSuggestions ?? 0),
  }
}

/**
 * Повторный разбор по сохранённой расшифровке — для встреч, разобранных до
 * появления оценок, и после правки промпта. Применённый черновик сервис не
 * трогает.
 *
 * Разбор длинной встречи моделью идёт минутами (Flex), а общие 30 с клиента
 * обрывали запрос, и бэкенд получал context canceled (DEV-2796). Ждём столько,
 * сколько держит ingress /api/v1/meetings — 900 с.
 */
const REQUALIFY_TIMEOUT_MS = 900_000

export async function requalifyMeeting(id: string): Promise<Meeting> {
  const { data } = await meetingsApi.meetingsServiceRequalifyMeeting(
    id,
    {},
    { timeout: REQUALIFY_TIMEOUT_MS },
  )

  return data
}

/** Что показывает пост-мит экран: запись, её расшифровка и разбор. */
export interface MeetingArtifacts {
  recordings: MeetingRecording[]
  transcripts: MeetingTranscript[]
}

/** Фильтр списка встреч карточки. Сделка или клиент — что открыто. */
export interface MeetingsQuery {
  clientId?: string
  dealId?: string
  statuses?: MeetingsServiceListMeetingsStatusesEnum[]
}

/**
 * Встречи карточки, свежие сверху: менеджеру нужна ближайшая запланированная и
 * последняя прошедшая, а не хвост полугодовой давности.
 */
export async function listMeetings(query: MeetingsQuery): Promise<Meeting[]> {
  const { data } = await meetingsApi.meetingsServiceListMeetings(
    query.clientId,
    query.dealId,
    undefined,
    undefined,
    query.statuses,
    undefined,
    undefined,
    true,
  )

  return data.meetings ?? []
}

export async function getMeeting(id: string): Promise<Meeting> {
  const { data } = await meetingsApi.meetingsServiceGetMeeting(id)

  return data
}

export async function scheduleMeeting(payload: MeetingsScheduleMeetingRequest): Promise<Meeting> {
  const { data } = await meetingsApi.meetingsServiceScheduleMeeting(payload)

  return data
}

export async function rescheduleMeeting(
  id: string,
  scheduledStart: string,
  scheduledEnd: string,
): Promise<Meeting> {
  const { data } = await meetingsApi.meetingsServiceRescheduleMeeting(id, {
    scheduledStart,
    scheduledEnd,
  })

  return data
}

export async function cancelMeeting(id: string, reason: string): Promise<Meeting> {
  const { data } = await meetingsApi.meetingsServiceCancelMeeting(id, { reason })

  return data
}

/** Отправки приглашения — бейджи доставки в строке встречи. */
export async function listInvites(meetingId: string): Promise<MeetingInvite[]> {
  const { data } = await meetingsApi.meetingsServiceListMeetingInvites(meetingId)

  return data.invites ?? []
}

/**
 * Повторная отправка. Пустой список каналов означает «те, что не дошли» — это
 * решает сервис, у него есть журнал попыток.
 */
export async function resendInvite(
  meetingId: string,
  channels: MeetingsInviteChannel[] = [],
): Promise<MeetingInvite[]> {
  const { data } = await meetingsApi.meetingsServiceResendInvite(meetingId, { channels })

  return data.invites ?? []
}

/**
 * Текст приглашения для ручной отправки. `markSent` отмечает, что менеджер
 * действительно отправил его сам: без отметки встречи с клиентом без каналов
 * выпали бы из учёта уведомлений о записи.
 */
export async function getInviteText(
  meetingId: string,
  locale?: string,
  markSent = false,
): Promise<string> {
  const { data } = await meetingsApi.meetingsServiceGetInviteText(meetingId, {
    locale,
    markSent,
  })

  return data.text ?? ''
}

/** Занятость менеджера в выбранном слоте — предупреждение в модалке. */
export async function checkAvailability(
  managerId: string,
  start: string,
  end: string,
): Promise<boolean> {
  const { data } = await meetingsApi.meetingsServiceCheckManagerAvailability(managerId, start, end)

  return data.busy ?? false
}

/** Записи встречи вместе с расшифровками — одним запросом, как их отдаёт API. */
export async function getArtifacts(meetingId: string): Promise<MeetingArtifacts> {
  const { data } = await meetingsApi.meetingsServiceListMeetingRecordings(meetingId)

  return { recordings: data.recordings ?? [], transcripts: data.transcripts ?? [] }
}

/**
 * Ссылка на аудио живёт около часа, поэтому берётся в момент открытия плеера, а
 * не складывается в состояние экрана.
 */
export async function getAudioUrl(meetingId: string, recordingId: string): Promise<string> {
  const { data } = await meetingsApi.meetingsServiceGetRecordingAudioUrl(meetingId, recordingId)

  return data.url ?? ''
}

export async function getQualification(meetingId: string): Promise<QualificationDraft | null> {
  const { data } = await meetingsApi.meetingsServiceGetQualification(meetingId)

  return data.draft ?? null
}

/**
 * Применяет выбранные поля черновика к карточке клиента, сделке и задачам.
 * Правки значений (overrides) — только для выбранных полей: модель услышала
 * вилку, менеджер знает цифру.
 */
export async function applyQualification(
  meetingId: string,
  fields: string[],
  overrides: QualificationOverride[] = [],
): Promise<QualificationDraft | null> {
  const { data } = await meetingsApi.meetingsServiceApplyQualification(meetingId, {
    fields,
    overrides,
  })

  return data.draft ?? null
}

export async function discardQualification(meetingId: string): Promise<QualificationDraft | null> {
  const { data } = await meetingsApi.meetingsServiceDiscardQualification(meetingId, {})

  return data.draft ?? null
}
